import type { OAuthGrantDto } from 'shared';
import { toast } from 'sonner';

import { useRevokeOAuthGrant } from '../../features/oauth/mutations';
import { useOAuthGrants } from '../../features/oauth/queries';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}

export function ConnectedAppsSection() {
  const grants = useOAuthGrants();
  const revoke = useRevokeOAuthGrant();

  function disconnect(grant: OAuthGrantDto) {
    if (!window.confirm(`Disconnect ${grant.clientName}? It will lose access immediately.`)) return;
    revoke.mutate(grant.id, {
      onSuccess: () => toast.success(`Disconnected ${grant.clientName}.`),
      onError: () => toast.error(`Could not disconnect ${grant.clientName}.`),
    });
  }

  return (
    <Card
      header={
        <div>
          <h2 className="text-lg font-semibold">Connected AI apps</h2>
          <p className="text-sm text-text-muted">
            Apps with read-only MCP access to your data. They cannot change anything or trade.
          </p>
        </div>
      }
    >
      {grants.isLoading ? (
        <p className="text-sm text-text-muted">Loading connected apps...</p>
      ) : grants.isError ? (
        <p className="text-sm text-danger">Could not load connected apps.</p>
      ) : grants.data && grants.data.length > 0 ? (
        <ul className="divide-y divide-border rounded border border-border">
          {grants.data.map((grant) => (
            <li
              key={grant.id}
              className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="text-sm">
                <p className="font-medium">{grant.clientName}</p>
                <p className="text-text-muted">
                  Connected {formatDate(grant.createdAt)} · Last token refresh{' '}
                  {formatDate(grant.lastUsedAt)}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                aria-label={`Disconnect ${grant.clientName}`}
                disabled={revoke.isPending}
                onClick={() => disconnect(grant)}
              >
                Disconnect
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-muted">No AI apps connected.</p>
      )}
    </Card>
  );
}
